import { type ReactElement } from "react";
import { useNavigate } from "react-router-dom";

import type { GetPatientResponse, UUID } from "@thalamos/aspire-common";

import { apiHooks } from "../api.js";
import { routeFns } from "../routePaths.js";

type WithPatientProps = {
  patientId: UUID | undefined;
  children: (patient: GetPatientResponse) => ReactElement;
};

export function WithPatient(props: WithPatientProps) {
  // Keyed so a different patient starts from fresh request state: the request
  // hook keeps the previous patient's data until the new one arrives, and the
  // loaded id cannot tell them apart, since a merged patient's id loads the
  // patient it was merged into.
  return <WithLoadedPatient key={props.patientId} {...props} />;
}

function WithLoadedPatient(props: WithPatientProps) {
  const [{ data: patient, loading: patientLoading }] = apiHooks.patients.get(
    props.patientId || "",
  );

  const navigate = useNavigate();

  // Reloading the patient already in hand must not unmount the children, or a
  // form below loses anything it has not yet saved.
  if (!props.patientId || (patientLoading && !patient)) {
    return <></>;
  } else if (patient) {
    return <>{props.children(patient)}</>;
  } else {
    navigate(routeFns.notFound(), { replace: true });
    return <></>;
  }
}
